import React from 'react';
import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { store } from '../../../store';
// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Toolbar, useMediaQuery } from '@mui/material';

// project import
import Drawer from './Drawer';
import Header from './Header';
import navigation from '../../../view-components/menu-items';
import Breadcrumbs from '../../../view-components/@extended/Breadcrumbs';
import Snackbar from '../../../views/landing/components/Snackbar';
import OnboardingOrchestrator from '../../onboarding/OnboardingOrchestrator';

import { fetchAuthSession } from 'aws-amplify/auth';
import { PLAN_MODIFIED, PLAN_CANCELED, ACCOUNT_DELETED } from '../../../graphql/subscriptions';
import { addSnackBarMessage, clearSnackBarMessage, setSnackBarOpen } from '../../../store/reducers/snackBarMessages';
import { gql, useApolloClient, useSubscription } from '@apollo/client';

// types
import { openDrawer } from '../../../store/reducers/menu';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { SubscriptionEventBus } from '../../../utils/subscriptionEventBus';
import { prepareSubscriptions } from '../../../utils/subscriptionRegistry';
import { logger } from '../../../utils/logger';

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const matchDownLG = useMediaQuery(theme.breakpoints.down('xl'));
    const { drawerOpen } = useSelector((state) => state.menu);
    const [tenantId, setTenantId] = useState('');
    const client = useApolloClient();
    const navigate = useNavigate();
    const { signOut } = useAuthenticator((context) => [context.route, context.signOut]);

    let snackBarOpen = useSelector((state) => state.snackBarMessages.snackBarOpen);
    let snackBarMessage = useSelector((state) => state.snackBarMessages.snackBarMessage);
    let snackBarMessageType = useSelector((state) => state.snackBarMessages.snackBarMessageType);
    let snackBarAction = useSelector((state) => state.snackBarMessages.snackBarAction);

    const handleSnackBarClose = () => {
        store.dispatch(setSnackBarOpen(false));
        store.dispatch(clearSnackBarMessage());
    };

    // drawer toggler
    const [open, setOpen] = useState(drawerOpen);
    const handleDrawerToggle = () => {
        setOpen(!open);
        dispatch(openDrawer({ drawerOpen: !open }));
    };

    useEffect(() => {
        if (open !== drawerOpen) setOpen(drawerOpen);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawerOpen]);

    // set media wise responsive drawer
    useEffect(() => {
        setOpen(!matchDownLG);
        dispatch(openDrawer({ drawerOpen: !matchDownLG }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchDownLG]);

    // Subscription Event Bus preperation
    useEffect(() => {
        fetchAuthSession().then((session) => {
            const getTenantId = session.tokens?.idToken?.payload?.name;
            setTenantId(getTenantId);
            const tenantId = session.tokens?.idToken?.payload?.name;
            //console.log('Tenant ID:', tenantId);
            const prepared = prepareSubscriptions(client, tenantId);

            Object.entries(prepared).forEach(([id, cb]) => SubscriptionEventBus.prepare(id, cb));
        });
    }, []);
    async function triggerTokenRefresh() {
        try {
            // Force refresh gets new tokens if refresh token is valid
            await fetchAuthSession({ forceRefresh: true });
            logger.log('session refreshing...');
        } catch (e) {
            logger.log('Unable to refresh Token', e);
        }
    }

    useSubscription(gql(PLAN_MODIFIED), {
        variables: { id: tenantId },
        onData: (data) => {
            logger.log('[PLAN_MODIFIED] Subscription data:', data);
            triggerTokenRefresh();
            store.dispatch(addSnackBarMessage('Your subscription plan has updated. Changes are reflected immediately.'));
            store.dispatch(setSnackBarOpen(true));
        },
        onError: (error) => {
            //console.log(tenantId);
            console.warn(error);
        }
    });

    useSubscription(gql(ACCOUNT_DELETED), {
        variables: { id: tenantId },
        onData: () => {
            store.dispatch(
                addSnackBarMessage('Account has been deleted. Any subscriptions purchased have been cancelled. You have been logged out.')
            );
            store.dispatch(setSnackBarOpen(true));
            signOut();
            navigate('/login');
        },
        onError: (error) => {
            logger.log(error);
            console.warn(error);
        }
    });

    useSubscription(gql(PLAN_CANCELED), {
        variables: { id: tenantId },
        onData: (data) => {
            logger.log('[PLAN_CANCELED] Subscription data:', data);
            store.dispatch(addSnackBarMessage('Your subscription has been canceled.'));
            store.dispatch(setSnackBarOpen(true));
            triggerTokenRefresh();
            navigate('/login');
        },
        onError: (error) => {
            //console.log(tenantId);
            console.warn(error);
        }
    });

    return (
        <Box sx={{ display: 'flex', width: '100%' }}>
            <Header open={open} handleDrawerToggle={handleDrawerToggle} />
            <Drawer open={open} handleDrawerToggle={handleDrawerToggle} />
            <Box component="main" sx={{ width: '100%', flexGrow: 1, p: { xs: 2, sm: 3 } }}>
                <Toolbar />
                <Breadcrumbs navigation={navigation} divider={false} />
                <Outlet />
            </Box>
            <Snackbar
                open={snackBarOpen}
                closeFunc={handleSnackBarClose}
                message={snackBarMessage}
                alertColor={snackBarMessageType}
                action={snackBarAction}
            />
            <OnboardingOrchestrator />
        </Box>
    );
};

export default MainLayout;
